import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Maximize2, Minimize2, RotateCw, X } from 'lucide-react'
import { activeOrg, apiBase } from '@/services/api'
import { isEnforcementOfficer } from '@/services/keycloakService'
import { useBranding } from '@/hooks/useBranding'
import { BrandIdentity } from '@/components/BrandIdentity'
import { PoweredByParkeze } from '@/components/PoweredByParkeze'
import { enterFullscreen, exitFullscreen } from '@/components/kiosk/fullscreen'
import { ModuleHost } from '@/shell/ModuleHost'
import { fetchMyApplications, type Application } from '@/shell/applications'
import { Listbox, ListboxButton } from '@/shell/Listbox'

const LIVE_BASE = import.meta.env.VITE_LIVE_API_URL || 'https://api.parkeze.com/v1'

const DESK_MODULE = 'reservation-desk'

/**
 * The desk application this browser last showed, when the organization runs
 * several. A convenience, not a boundary: an id the organization no longer
 * runs asks again, and the services decide what the account may do whatever
 * this says.
 */
const DESK_KEY = 'parkeze.kiosk.application'

function readDesk(): string | null {
  try { return localStorage.getItem(DESK_KEY) } catch { return null }
}

function writeDesk(id: string) {
  try { localStorage.setItem(DESK_KEY, id) } catch { /* the choice just won't persist */ }
}

function Clock() {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1_000)
    return () => clearInterval(t)
  }, [])
  return (
    <div className="flex flex-col items-center leading-tight">
      <span className="font-mono text-xl font-semibold tabular-nums">
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
      <span className="text-xs text-muted-foreground">
        {now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
      </span>
    </div>
  )
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">{children}</div>
}

/**
 * The station-less desk (logbook decision 21): a `reservation-desk`
 * application the person's organization registered, mounted from
 * modules.parkeze.com at that application's major over that application's
 * zones. What is here is the shell's chrome around it: branding, which desk
 * (when the organization runs several), the clock, full screen and the way
 * back. The module draws its own row below -- the zones, Check plate, Book.
 *
 * Two gates: the reservation roles (the desk's checks and violation stream
 * need them), and an application -- an organization that registered none
 * gets no desk.
 *
 * Without a station nothing narrows the account: what the person may do is
 * their roles' (logbook decision 24), and the API's `available_actions` and
 * refusals decide, as always.
 */
export default function KioskPage() {
  const navigate = useNavigate()
  const { brand } = useBranding()
  const officer = isEnforcementOfficer()
  const org = activeOrg()

  const applications = useQuery({
    queryKey: ['applications', 'mine', 'enforcement', DESK_MODULE, org],
    queryFn: () => fetchMyApplications({ surface: 'enforcement', module: DESK_MODULE }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: officer,
  })
  const desks = useMemo(
    () => (applications.data ?? []).filter(
      (a): a is Application & { major: number } => a.module === DESK_MODULE && Number.isInteger(a.major),
    ),
    [applications.data],
  )

  const [deskId, setDeskId] = useState<string | null>(readDesk)
  // One desk needs no choosing; of several, the one this device chose.
  const desk = desks.length === 1 ? desks[0] : desks.find((d) => d.id === deskId) ?? null
  const choose = (id: string) => {
    setDeskId(id)
    writeDesk(id)
  }
  const options = useMemo(() => desks.map((d) => ({ id: d.id, label: d.name })), [desks])

  const [fullscreen, setFullscreen] = useState(!!document.fullscreenElement)

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  if (!officer) {
    return (
      <div className="h-dvh">
        <Centered>
          <p className="text-lg font-semibold">The kiosk is for enforcement officers.</p>
          <button type="button" onClick={() => navigate('/home')} className="text-primary underline">
            Back to reservations
          </button>
        </Centered>
      </div>
    )
  }

  const exit = () => {
    exitFullscreen()
    navigate('/home')
  }

  let body: ReactNode
  if (applications.isLoading) {
    body = (
      <Centered>
        <span className="flex items-center gap-2 text-muted-foreground" role="status">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading…</span>
        </span>
      </Centered>
    )
  } else if (applications.error) {
    body = (
      <Centered>
        <p className="text-lg font-semibold" role="alert">{(applications.error as Error).message}</p>
        <button
          type="button"
          onClick={() => void applications.refetch()}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium"
        >
          <RotateCw className="w-4 h-4" /> Try again
        </button>
      </Centered>
    )
  } else if (!desks.length) {
    body = (
      <Centered>
        <p className="text-lg font-semibold">Your organization hasn&rsquo;t set up a reservation desk.</p>
      </Centered>
    )
  } else if (!desk) {
    body = (
      <Centered>
        <div className="w-full max-w-sm text-left space-y-3">
          <h1 className="text-lg font-semibold text-center">Choose a reservation desk</h1>
          <div className="rounded-xl border border-border bg-card p-2 shadow-sm">
            <Listbox options={options} selectedId={null} onSelect={choose} label="Reservation desk" autoFocus />
          </div>
        </div>
      </Centered>
    )
  } else if (!desk.zone_ids.length) {
    body = (
      <Centered>
        <p className="text-lg font-semibold">{desk.name} has no zones.</p>
      </Centered>
    )
  } else {
    body = (
      <ModuleHost
        key={desk.id}
        id={DESK_MODULE}
        major={desk.major}
        label="reservation desk"
        attributes={{
          'api-base': apiBase(),
          'live-base': LIVE_BASE,
          'active-org': org,
          'zone-ids': desk.zone_ids.join(','),
        }}
      />
    )
  }

  return (
    <div className="relative h-dvh flex flex-col">
      {/* Who · which desk · what time it is · the way out. The clock sits in
          its own middle column so it stays centered whatever the desk name. */}
      <header className="relative z-40 grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_1fr] items-center gap-x-4 gap-y-2 px-4 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex min-w-0 shrink-0 max-w-[40%] lg:max-w-none">
            <BrandIdentity brand={brand} />
          </span>
          {desk && (
            <>
              <span className="h-6 w-px bg-border shrink-0" aria-hidden />
              {desks.length > 1 ? (
                <ListboxButton options={options} selectedId={desk.id} onSelect={choose} label="Reservation desk" />
              ) : (
                <span className="px-2 py-1 text-base font-semibold truncate">{desk.name}</span>
              )}
            </>
          )}
        </div>

        <div className="justify-self-end md:justify-self-center">
          <Clock />
        </div>

        <div className="col-span-2 md:col-span-1 flex items-center justify-end gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => (fullscreen ? exitFullscreen() : enterFullscreen())}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={fullscreen ? 'Leave full screen' : 'Full screen'}
            title={fullscreen ? 'Leave full screen' : 'Full screen'}
          >
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={exit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Exit kiosk"
            title="Exit kiosk"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0">{body}</div>
      <PoweredByParkeze />
    </div>
  )
}
